import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, LogIn, User, UserPlus } from "lucide-react";
import { useCart, effectiveUnitPrice } from "../lib/CartContext";
import { useSettings } from "../lib/SettingsContext";
import { useCustomer, withAuth } from "../lib/CustomerContext";
import api, { formatApiErrorDetail } from "../lib/api";
import SEO from "../lib/SEO";

export default function Checkout() {
    const { items, subtotal, clear } = useCart();
    const { settings } = useSettings();
    const { customer } = useCustomer();

    // Cake in a Jar has its own postage pricing by quantity, separate from the
    // site-wide flat delivery fee — only applies when the cart is jars only.
    const isJarOnlyCart = items.length > 0 && items.every((it) => it.category === "Cake in a Jar");
    const jarQty = items.reduce((s, it) => s + it.qty, 0);
    const jarPostageFee = jarQty <= 1 ? 15 : jarQty === 2 ? 20 : 25;

    // Cupcakes and Cookies aren't set up for automated delivery pricing — pickup
    // only through checkout, delivery is quoted manually. Only forced when the
    // cart is entirely these items, so it doesn't block a mixed cart's delivery.
    const isPickupOnlyCart =
        items.length > 0 && items.every((it) => it.category === "Cupcakes" || it.category === "Cookies");

    const rawDeliveryFee = Number(settings?.delivery_fee) || 0;
    const freeMin = Number(settings?.free_delivery_min) || 0;
    const standardDeliveryFee = freeMin > 0 && subtotal >= freeMin ? 0 : rawDeliveryFee;
    const navigate = useNavigate();
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        delivery_address: "",
        delivery_date: "",
        notes: "",
        payment_method: "manual",
        fulfillment_type: "delivery",
        cake_message: "",
        colour_notes: "",
        base_colour: "",
        piped_colour: "",
    });
    const [saveDetails, setSaveDetails] = useState(false);
    const [savePassword, setSavePassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [placedOrder, setPlacedOrder] = useState(null);

    // Bento Cakes need extra ordering info (cake message, colour notes, 2-day notice)
    const hasBento = items.some((it) => it.category === "Bento Cakes");
    // Vintage Lambeth Cookies need base/piped colour, a message, and 7 days notice
    const hasCookies = items.some((it) => it.category === "Cookies");
    const minDeliveryDate = (() => {
        const d = new Date();
        d.setDate(d.getDate() + (hasBento ? 2 : hasCookies ? 7 : 0));
        return d.toISOString().slice(0, 10);
    })();

    // Keep the typed delivery address intact when toggling to Pickup and back —
    // only the effective value sent to the server swaps, via effectiveAddress below.
    const pickupAddress = settings?.pickup_address || "Hinchinbrook, NSW";
    const effectiveAddress = form.fulfillment_type === "pickup" ? pickupAddress : form.delivery_address;

    const isPickup = form.fulfillment_type === "pickup";
    const deliveryFee = isPickup ? 0 : (isJarOnlyCart ? jarPostageFee : standardDeliveryFee);
    const total = subtotal + deliveryFee;

    // Cupcakes/Cookies-only carts aren't set up for automated delivery — force pickup.
    useEffect(() => {
        if (isPickupOnlyCart && form.fulfillment_type !== "pickup") {
            setForm((f) => ({ ...f, fulfillment_type: "pickup" }));
        }
    }, [isPickupOnlyCart]);

    // Prefill form when a customer is logged in (or when their profile loads)
    useEffect(() => {
        if (customer) {
            setForm((f) => ({
                ...f,
                customer_name: f.customer_name || customer.name || "",
                customer_email: f.customer_email || customer.email || "",
                customer_phone: f.customer_phone || customer.phone || "",
                delivery_address: f.delivery_address || customer.default_address || "",
            }));
        }
    }, [customer]);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                delivery_address: effectiveAddress,
                items: items.map((it) => ({
                    product_id: it.product_id,
                    name: it.name,
                    price: effectiveUnitPrice(it.price, it.price_tiers, it.qty),
                    qty: it.qty,
                    option: it.option || null,
                    size_label: it.size_label || null,
                })),
            };
            const r = await api.post("/orders", payload, withAuth());
            const order = r.data;

            // Offer to save account from guest details
            if (!customer && saveDetails && savePassword.length >= 8) {
                try {
                    await api.post("/customer/register", {
                        name: form.customer_name,
                        email: form.customer_email,
                        password: savePassword,
                    });
                    toast.success("Account created — you can now log in with your email");
                } catch (regErr) {
                    // Not fatal — the order still went through
                    toast.info("Order placed. (Account creation skipped — email may already exist.)");
                }
            }

            if (form.payment_method === "stripe") {
                // Redirect to Stripe's hosted checkout — payment isn't confirmed yet,
                // so don't clear the cart or show the success screen until the webhook
                // marks the order paid and the customer lands back on the tracking page.
                const checkoutRes = await api.post(`/orders/${order.id}/stripe-checkout`);
                window.location.href = checkoutRes.data.url;
                return;
            }

            clear();
            setPlacedOrder(order);
            toast.success(`Order ${order.order_number} placed!`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to place order");
        } finally {
            setSubmitting(false);
        }
    };

    // ─── SUCCESS SCREEN ────────────────────────────────────────────────
    if (placedOrder) {
        return <OrderPlaced order={placedOrder} />;
    }

    if (items.length === 0) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center">
                <SEO title="Checkout — Cake Central" path="/checkout" />
                <h2 className="font-display text-3xl mb-4">Your cart is empty</h2>
                <Link to="/shop" className="btn-pink mt-4">Browse the shop</Link>
            </main>
        );
    }

    return (
        <main data-testid="checkout-page" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[60vh]">
            <SEO title="Checkout — Cake Central" path="/checkout" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="font-display text-4xl md:text-5xl text-ink mb-6 leading-none">Checkout</h1>

                {/* Auth choice banner — only visible to guests */}
                {!customer && (
                    <div className="bg-white border border-blush/30 p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-ink">
                            <span className="font-medium">Have an account?</span>
                            <span className="text-ink-soft ml-2">Log in to skip filling this form.</span>
                        </p>
                        <div className="flex gap-3">
                            <Link
                                to="/login?next=/checkout"
                                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-blush hover:underline"
                            >
                                <LogIn size={14} /> Log in
                            </Link>
                            <Link
                                to="/signup?next=/checkout"
                                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink hover:text-blush"
                            >
                                <UserPlus size={14} /> Sign up
                            </Link>
                        </div>
                    </div>
                )}
                {customer && (
                    <div className="bg-white border border-emerald-200 p-3 mb-6 flex items-center gap-2 text-sm">
                        <User size={14} className="text-emerald-600" />
                        <span>Signed in as <strong>{customer.name}</strong> ({customer.email})</span>
                    </div>
                )}

                <div className="grid md:grid-cols-[1fr_320px] gap-10">
                    <form onSubmit={onSubmit} className="space-y-5">
                        <div className="bg-white border border-[#EAE3DE] p-6 space-y-4">
                            <h2 className="font-display text-xl mb-2">Your details</h2>
                            <Field label="Full Name *" name="customer_name" value={form.customer_name} onChange={onChange} required />
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Email *" name="customer_email" type="email" value={form.customer_email} onChange={onChange} required />
                                <Field label="Phone *" name="customer_phone" type="tel" value={form.customer_phone} onChange={onChange} required />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                                    Pickup or {isJarOnlyCart ? "Postage" : "Delivery"}? *
                                </label>
                                {isJarOnlyCart && (
                                    <p className="text-[11px] text-ink-soft mb-2">
                                        Postage: 1 jar $15 · 2 jars $20 · 3+ jars $25
                                    </p>
                                )}
                                {isPickupOnlyCart && (
                                    <p className="text-[11px] text-ink-soft mb-2">
                                        If delivery is required please email{" "}
                                        <a href="mailto:info@cakecentral.com.au" className="text-blush hover:underline">info@cakecentral.com.au</a>{" "}
                                        or SMS 0410 477 455 with the location and we will send you a quote.
                                    </p>
                                )}
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        disabled={isPickupOnlyCart}
                                        onClick={() => setForm({ ...form, fulfillment_type: "delivery" })}
                                        className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                                            isPickupOnlyCart
                                                ? "bg-[#F5F0EC] border-[#EAE3DE] text-ink-soft/50 cursor-not-allowed"
                                                : form.fulfillment_type === "delivery" ? "bg-blush text-white border-blush" : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                                        }`}
                                        data-testid="fulfillment-delivery"
                                    >
                                        {isJarOnlyCart ? "Postage" : "Delivery"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, fulfillment_type: "pickup" })}
                                        className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                                            form.fulfillment_type === "pickup" ? "bg-blush text-white border-blush" : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                                        }`}
                                        data-testid="fulfillment-pickup"
                                    >
                                        Pickup
                                    </button>
                                </div>
                            </div>

                            {form.fulfillment_type === "pickup" ? (
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Pickup Location</label>
                                    <p className="px-4 py-3 bg-blush-pale/30 border border-[#EAE3DE] text-ink" data-testid="pickup-address">
                                        {pickupAddress}
                                    </p>
                                </div>
                            ) : (
                                <Field label={isJarOnlyCart ? "Postage Address *" : "Delivery Address *"} name="delivery_address" value={form.delivery_address} onChange={onChange} required />
                            )}

                            <Field
                                label="Delivery / Pickup Date"
                                name="delivery_date"
                                type="date"
                                value={form.delivery_date}
                                onChange={onChange}
                                min={minDeliveryDate}
                                helper={
                                    hasBento
                                        ? "We require at least 2 days notice for Bento Cakes."
                                        : hasCookies
                                            ? "We require at least 7 days notice for Vintage Lambeth Cookies."
                                            : undefined
                                }
                            />

                            {hasBento && (
                                <>
                                    <Field
                                        label="Cake Message *"
                                        name="cake_message"
                                        value={form.cake_message}
                                        onChange={onChange}
                                        required
                                        minLength={8}
                                        helper="This is what we'll pipe on the cake — minimum 8 characters."
                                        testId="cake-message"
                                    />
                                    <Field
                                        label="Colour / Design Notes (optional)"
                                        name="colour_notes"
                                        textarea
                                        value={form.colour_notes}
                                        onChange={onChange}
                                        testId="colour-notes"
                                    />
                                </>
                            )}

                            {hasCookies && (
                                <>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <Field
                                            label="Base Colour (2 colours)"
                                            name="base_colour"
                                            value={form.base_colour}
                                            onChange={onChange}
                                            testId="base-colour"
                                        />
                                        <Field
                                            label="Piped Colour (alternating)"
                                            name="piped_colour"
                                            value={form.piped_colour}
                                            onChange={onChange}
                                            testId="piped-colour"
                                        />
                                    </div>
                                    <Field
                                        label="Cookie Messages"
                                        name="cake_message"
                                        textarea
                                        value={form.cake_message}
                                        onChange={onChange}
                                        testId="cookie-message"
                                    />
                                </>
                            )}

                            <Field label="Notes (allergies, message, etc)" name="notes" textarea value={form.notes} onChange={onChange} />
                        </div>

                        <div className="bg-white border border-[#EAE3DE] p-6">
                            <h2 className="font-display text-xl mb-4">Payment method</h2>
                            <div className="space-y-3">
                                <PaymentOption
                                    selected={form.payment_method === "manual"}
                                    onClick={() => setForm({ ...form, payment_method: "manual" })}
                                    label="Pay on confirmation"
                                    description="We'll email you a payment link or bank transfer details once the order is confirmed."
                                />
                                <PaymentOption
                                    selected={form.payment_method === "stripe"}
                                    onClick={() => setForm({ ...form, payment_method: "stripe" })}
                                    label="Card (Stripe)"
                                    description="Pay securely by credit/debit card — you'll be redirected to Stripe to complete payment."
                                />
                                <PaymentOption
                                    selected={form.payment_method === "paypal"}
                                    onClick={() => setForm({ ...form, payment_method: "paypal" })}
                                    label="PayPal"
                                    description="Pay via your PayPal account."
                                    disabled
                                />
                            </div>
                            <p className="text-[11px] text-ink-soft mt-4 italic">
                                Online PayPal payments coming soon.
                            </p>
                        </div>

                        {/* Save my details — only shown to guests */}
                        {!customer && (
                            <div className="bg-white border border-[#EAE3DE] p-6">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={saveDetails}
                                        onChange={(e) => setSaveDetails(e.target.checked)}
                                        className="mt-1"
                                        data-testid="save-details-checkbox"
                                    />
                                    <div className="flex-1">
                                        <p className="font-medium text-ink">Save my details for next time</p>
                                        <p className="text-xs text-ink-soft">
                                            Creates an account with this email so you can track your order and check out faster next time.
                                        </p>
                                    </div>
                                </label>
                                {saveDetails && (
                                    <div className="mt-4 pl-7">
                                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Choose a password (min 8 chars)</label>
                                        <input
                                            type="password"
                                            minLength={8}
                                            required={saveDetails}
                                            value={savePassword}
                                            onChange={(e) => setSavePassword(e.target.value)}
                                            className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                                            data-testid="save-details-password"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <button type="submit" disabled={submitting} className="btn-pink w-full justify-center disabled:opacity-60" data-testid="place-order-btn">
                            {submitting ? "Placing order…" : `Place order — $${total.toFixed(2)} AUD`}
                        </button>
                    </form>

                    <aside className="bg-white border border-[#EAE3DE] p-6 h-fit sticky top-32">
                        <h2 className="font-display text-xl mb-4">Order summary</h2>
                        <div className="space-y-3 mb-5">
                            {items.map((it) => {
                                const perUnit = effectiveUnitPrice(it.price, it.price_tiers, it.qty);
                                return (
                                    <div key={it.product_id} className="flex justify-between gap-3 text-sm">
                                        <span className="text-ink line-clamp-2">{it.name} × {it.qty}</span>
                                        <span className="font-medium shrink-0">${(it.qty * perUnit).toFixed(2)}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="h-px bg-[#EAE3DE] mb-3" />
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-ink-soft">Subtotal</span>
                            <span>${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm py-1">
                            <span className="text-ink-soft">{isJarOnlyCart ? "Postage" : "Delivery"}</span>
                            <span>
                                {isPickup
                                    ? <span className="text-emerald-600">FREE</span>
                                    : isJarOnlyCart
                                        ? `$${deliveryFee.toFixed(2)}`
                                        : (rawDeliveryFee > 0
                                            ? (deliveryFee === 0
                                                ? <span className="text-emerald-600">FREE</span>
                                                : `$${deliveryFee.toFixed(2)}`)
                                            : "—")}
                            </span>
                        </div>
                        <div className="h-px bg-[#EAE3DE] my-3" />
                        <div className="flex justify-between font-display text-lg">
                            <span>Total</span>
                            <span>${total.toFixed(2)} AUD</span>
                        </div>
                    </aside>
                </div>
            </div>
        </main>
    );
}

function OrderPlaced({ order }) {
    const trackUrl = `${window.location.origin}/#/track/${order.tracking_token}`;
    const copyLink = () => {
        navigator.clipboard.writeText(trackUrl);
        toast.success("Link copied");
    };
    return (
        <main data-testid="order-placed" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[70vh]">
            <SEO title={`Order ${order.order_number} — Cake Central`} path="/checkout" />
            <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
                <CheckCircle size={64} className="mx-auto mb-6 text-emerald-500" />
                <h1 className="font-display text-4xl md:text-5xl text-ink mb-3 leading-none">Thank you!</h1>
                <p className="text-ink-soft mb-2">Your order has been placed.</p>
                <p className="font-display text-2xl text-blush mb-8">{order.order_number}</p>

                <div className="bg-white border border-[#EAE3DE] p-6 text-left space-y-4">
                    <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Total</p>
                        <p className="font-display text-3xl">${order.total.toFixed(2)} AUD</p>
                    </div>
                    <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Track your order</p>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                readOnly
                                value={trackUrl}
                                className="flex-1 px-3 py-2 border border-[#EAE3DE] text-sm bg-blush-pale/30"
                                onFocus={(e) => e.target.select()}
                            />
                            <button onClick={copyLink} className="btn-pink btn-pink-outline text-xs px-4">Copy</button>
                        </div>
                        <p className="text-[11px] text-ink-soft mt-2">
                            Save this link — you can also find it in your confirmation email.
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 justify-center mt-8">
                    <Link to={`/track/${order.tracking_token}`} className="btn-pink" data-testid="view-order-btn">View order</Link>
                    <Link to="/shop" className="btn-pink btn-pink-outline">Continue shopping</Link>
                </div>
            </div>
        </main>
    );
}

function Field({ label, name, value, onChange, type = "text", required = false, textarea = false, min, minLength, helper, testId }) {
    const inputClass = "w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink";
    return (
        <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">{label}</label>
            {textarea ? (
                <textarea name={name} rows={3} value={value} onChange={onChange} className={inputClass + " resize-none"} data-testid={testId} />
            ) : (
                <input
                    type={type}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required={required}
                    min={min}
                    minLength={minLength}
                    className={inputClass}
                    data-testid={testId}
                />
            )}
            {helper && <p className="text-[11px] text-ink-soft mt-1.5">{helper}</p>}
        </div>
    );
}

function PaymentOption({ selected, onClick, label, description, disabled = false }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={`w-full text-left p-4 border transition-colors ${
                selected ? "border-blush bg-blush-pale/30" : "border-[#EAE3DE]"
            } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-blush"}`}
        >
            <div className="flex items-center gap-3">
                <span className={`w-4 h-4 rounded-full border-2 ${selected ? "border-blush bg-blush" : "border-[#EAE3DE]"}`} />
                <span className="font-medium text-ink">{label}</span>
                {disabled && <span className="text-[10px] tracking-[0.2em] uppercase text-ink-soft">Coming soon</span>}
            </div>
            <p className="text-xs text-ink-soft mt-1 pl-7">{description}</p>
        </button>
    );
}
